import { BrowserRouter, Route } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material';

import LoginWrapper from '@/ui/login/LoginWrapper';
import LoginScreen from '@/ui/screens/LoginScreen';
import LogoutScreen from '@/ui/screens/LogoutScreen';
import ResetPasswordScreen from '@/ui/screens/ResetPasswordScreen';
import PromotionList from '@/ui/screens/PromotionList';
import UserAccount from '@/ui/screens/UserAccount';
import EditAccount from '@/ui/screens/EditAccount';
import StorePropertyList from '@/ui/screens/StorePropertyList';
import { MaintenanceMode } from '@/components/maintenance-mode';
import ManageStoreProperty from '@/ui/screens/ManageStoreProperty';
import { AuthProvider } from '@/components/auth-provider';
import theme from '@/ui/theme';
import TailwindIndicator from '@/components/tailwind-indicator';
import { Toaster } from '@/components/ui/sonner';
import { PublicRoute } from '@/components/public-route';
import { PrivateRoute } from '@/components/private-route';
import { SentryRoutes } from '@/utils/sentry';
import NavBar from '@/ui/navbar';
import { AnnouncementBanner } from '@/components/announcement-banner';
import { DowntimeBanner } from '@/components/downtime-banner';
import { NotFound } from '@/components/not-found';
import { useFeatureFlags } from '@/lib/feature-flags';
import { queryClient } from '@/lib/query-client';

export function AppRoutes() {
  const { bannerAnnouncement, maintenanceMode } = useFeatureFlags();

  if (maintenanceMode) {
    return <MaintenanceMode />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <DowntimeBanner />
            <SentryRoutes>
              <Route element={<PublicRoute />}>
                <Route element={<LoginWrapper />}>
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordScreen />}
                  />
                  <Route path="/" element={<LoginScreen />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute />}>
                <Route
                  element={
                    <>
                      {bannerAnnouncement.enabled && (
                        <AnnouncementBanner href={bannerAnnouncement.href} />
                      )}
                      <NavBar />
                    </>
                  }
                >
                  <Route path="/promotions/*" element={<PromotionList />} />
                  <Route path="/account" element={<UserAccount />} />
                  <Route path="/account/edit" element={<EditAccount />} />
                  <Route path="/attributes" element={<StorePropertyList />} />
                  <Route
                    path="/attributes/create"
                    element={<ManageStoreProperty />}
                  />
                  <Route
                    path="/attributes/edit/:id"
                    element={<ManageStoreProperty />}
                  />
                  <Route path="/logout" element={<LogoutScreen />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
            <TailwindIndicator />
            <Toaster position="top-center" richColors />
          </ThemeProvider>
        </QueryClientProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
